import styled from '@emotion/styled'

/**
 * UnavailableResultStep — 수거 불가능 결과 화면 본문 (Figma 16294:42184).
 *
 * 검색 인풋은 부모(AddressSearchModal)가 그대로 그림. 본 styles는 본문 영역만.
 *
 * 본문 layout:
 *  - flex 1, justify-between
 *  - 상단 그룹: 일러스트 + 메시지 + 주소칩 + 카운터 + CTA
 *  - 하단: "서비스 가능 지역 보기" 링크
 */

// ─── 본문 ────────────────────────────────────────────────

export const BodyWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 48px 20px 24px;
`

export const TopGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
`

export const InstructionGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
`

export const InstructionText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

export const Headline = styled.p`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  line-height: 38px;
  letter-spacing: -0.42px;
  color: #1f1f1f;
  text-align: center;
  white-space: pre-line;
`

export const AddressChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid #e4e5e7;
  border-radius: 9999px;
  background: #ffffff;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: -0.075px;
  color: #1b1c1d;
`

// ─── 카운터 + CTA ───────────────────────────────────────

/**
 * 카운터 + CTA + 캡션 묶음.
 *
 * `data-loading='true'` (waitlist 응답 전): visibility hidden — 영역 자리는 잡되
 * 잘못된 default 값(isCompleted=false 등) 으로 잠깐 활성 CTA 가 노출되는 깜빡임 방어.
 * 응답 도착 시 visible — layout shift 없이 정확한 상태로 즉시 전환.
 */
export const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;

  &[data-loading='true'] {
    visibility: hidden;
  }
`

/**
 * 카운터 박스 — 사람 아이콘(NeighborIcon, currentColor) + 텍스트.
 *
 * `data-empty` attribute 로 두 케이스 분기 (Figma 17059:189351 vs 17059:189427):
 *  - 기본 (waitingCount > 0): color #2f3032 (어두운 회색) — 아이콘 + 텍스트 동일 색
 *  - empty (waitingCount === 0): color #5d5f65 (fg/neutral/tertiary) — 약하게
 * NeighborIcon 은 currentColor 라 부모 color 그대로 따라옴.
 */
export const CounterBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 16px;
  background: #f5f5f6;
  border-radius: 10px;
  color: #2f3032;

  &[data-empty='true'] {
    color: #5d5f65;
  }
`

/**
 * 카운터 텍스트 — 색상은 부모(CounterBox)에서 상속.
 * strong 만 brand orange 명시 (waitingCount 강조).
 */
export const CounterText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: inherit;
  text-align: center;

  & > strong {
    color: #ff5626;
    font-weight: 700;
  }
`

export const CtaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`

/**
 * 오픈 요청 CTA — 상태별 시각 분기.
 *
 *  - 기본: 브랜드 오렌지 + 흰 텍스트 + Bold
 *  - data-state='loading' (mutation 진행 중): opacity 낮춤
 *  - data-state='completed' (이미 신청, Figma 17059:189351):
 *      배경 #e4e5e7 (border/disabled), 텍스트 #acaeb4 (fg/neutral/disabled), Semibold
 *      — 단순 disabled opacity 와 다른 명시적 "이미 신청" 시각
 *
 * `disabled` attribute 는 a11y 위해 그대로 두고 시각만 data-state 로 제어.
 */
export const CtaButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 56px;
  max-height: 60px;
  padding: 12px 32px;
  background: #ff5626;
  color: #ffffff;
  border: 0;
  border-radius: 16px;
  font-size: 17px;
  font-weight: 700;
  line-height: 26px;
  letter-spacing: -0.17px;
  cursor: pointer;

  &:active {
    opacity: 0.85;
  }

  &[data-state='loading'] {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &[data-state='completed'] {
    background: #e4e5e7;
    color: #acaeb4;
    font-weight: 600;
    cursor: not-allowed;
  }
`

export const CtaCaption = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #acaeb4;
  text-align: center;
`

// ─── 하단 링크 ──────────────────────────────────────────

export const BottomLinkButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border: 0;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #888888;
  text-decoration: underline;
  cursor: pointer;

  &:active {
    opacity: 0.7;
  }
`
